import React, { useCallback, useEffect, useRef, useState } from 'react'
import { IconButton, Preview, Typogaphy } from 'vnetwork-ui'
import CloseIcons from '@/icons/CloseIcons'
import { useTranslation } from 'react-i18next'

const MIN_WIDTH = 320
const MAX_WIDTH = 800
const DEFAULT_WIDTH = 450
const STORAGE_KEY = 'help-panel-width'

interface HelpPanelProps {
    isOpen: boolean
    content: string
    title?: string
    onClose: () => void
}

const HelpPanel: React.FC<HelpPanelProps> = ({
    isOpen,
    content,
    title,
    onClose,
}) => {
    const { t } = useTranslation()
    const panelRef = useRef<HTMLDivElement>(null)
    const resizerRef = useRef<HTMLDivElement>(null)
    const [width, setWidth] = useState(() => {
        const saved = localStorage.getItem(STORAGE_KEY)
        return saved ? parseInt(saved, 10) : DEFAULT_WIDTH
    })
    const [isResizing, setIsResizing] = useState(false)

    // Save width to localStorage when it changes
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, width.toString())
    }, [width])

    // Handle click outside to close
    useEffect(() => {
        if (!isOpen) return

        const handleClickOutside = (e: MouseEvent) => {
            if (
                panelRef.current &&
                !panelRef.current.contains(e.target as Node) &&
                !isResizing
            ) {
                onClose()
            }
        }

        // Delay để tránh đóng ngay khi click mở
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside)
        }, 100)

        return () => {
            clearTimeout(timer)
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen, isResizing, onClose])

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        setIsResizing(true)
    }, [])

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isResizing) return

            const newWidth = window.innerWidth - e.clientX
            if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
                setWidth(newWidth)
            }
        },
        [isResizing]
    )

    const handleMouseUp = useCallback(() => {
        setIsResizing(false)
    }, [])

    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
            document.body.style.cursor = 'col-resize'
            document.body.style.userSelect = 'none'
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
        }
    }, [isResizing, handleMouseMove, handleMouseUp])

    // Handle escape key to close
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose()
            }
        }
        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [isOpen, onClose])

    if (!isOpen) return null

    return (
        <>
            {/* Backdrop for mobile */}
            <div
                className="fixed inset-0 bg-black/20 z-[9998] lg:hidden"
                onClick={onClose}
            />

            {/* Panel */}
            <div
                ref={panelRef}
                className="fixed top-0 right-0 h-svh z-[9999] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out"
                style={{
                    width: `${width}px`,
                    transform: isOpen
                        ? 'translateX(0)'
                        : `translateX(${width}px)`,
                }}
            >
                {/* Resizer handle */}
                <div
                    ref={resizerRef}
                    onMouseDown={handleMouseDown}
                    className={`absolute left-0 top-0 h-full w-1 cursor-col-resize group hover:bg-primary/50 transition-colors ${
                        isResizing ? 'bg-primary' : 'bg-transparent'
                    }`}
                >
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-12 -ml-1.5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-1 h-8 bg-gray-300 rounded-full" />
                    </div>
                </div>

                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 shrink-0">
                    <div className="flex items-center gap-2">
                        <svg
                            className="w-5 h-5 text-primary"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <Typogaphy className="font-semibold text-gray-800">
                            {title || t('help-guide')}
                        </Typogaphy>
                    </div>
                    <IconButton
                        size="small"
                        onClick={onClose}
                        className="hover:bg-gray-200 rounded-full"
                    >
                        <CloseIcons className="w-4 h-4" />
                    </IconButton>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    <div className="p-4 prose prose-sm max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-li:text-gray-600 prose-strong:text-gray-800 prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:before:content-none prose-code:after:content-none">
                        <Preview
                            content={content}
                            scrollElement={document.body}
                        />
                    </div>
                </div>

                {/* Footer hint */}
                <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 shrink-0">
                    <Typogaphy size="small" className="text-gray-400 text-xs">
                        {t('help-panel-hint')}
                    </Typogaphy>
                </div>
            </div>
        </>
    )
}

export default HelpPanel
